import { JurisdictionModel } from "../../settings/models/jurisdiction";
import { UserSettingsModel } from "../../settings/models/user-settings";
import { autoinject } from "aurelia-dependency-injection";
import { backboneViewProperties } from "../../decorators/backbone-properties";
import Marionette from "../../shared/marionette-mustache";
import { ThinkerView } from "../../shared/views/thinker";

const template = require<string>("../../settings/views/jurisdiction-settings.html");

const dom = {
    SEARCH_BOX: "#jurisdictions-search",
    CHECKBOXES: ".jurisdiction-check",
    CHILD_CHECKBOXES: ".jurisdiction-child-check",
    CHECKALL: ".jurisdiction-check-all",
    LOADING_REGION: "#jurisdictions-loading-region",
    CONTENT_REGION: "#jurisdictions-content-region",
    CHECK_ALL_OTHER_JURISDICTION: "#include-all-other-jurisdiction",
    MORE_LINKS: ".jurisdiction-more",
    SETTINGS_CONTENT: ".settings-content-container",
    RETURN_BUTTON: ".settings-return",
    SAVE_ORG_SETTINGS: ".save-organisation-jurisdictions-button",
    SELECT_ALL_BUTTON: "#select-all-jurisdictions"
};

@autoinject
@backboneViewProperties({
    template: template
})
export class JurisdictionSettingsView extends Marionette.LayoutView<UserSettingsModel> {

    private filterTimer: any;
    private loadingRegion: Marionette.Region;

    constructor(public model: UserSettingsModel) {
        super();
        this.setupModelChangeListeners();
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = {};
        events["click " + dom.CHECKBOXES] = (e) => this.jurisdictionSelectClick(e);
        events["click " + dom.CHILD_CHECKBOXES] = (e) => this.jurisdictionChildSelectClick(e);
        events["click " + dom.CHECK_ALL_OTHER_JURISDICTION] = (e) => this.jurisdictionSelectOthersClick(e);
        events["keyup " + dom.SEARCH_BOX] = (e) => this.searchBoxKeyup(e);
        events["click " + dom.MORE_LINKS] = (e) => this.jurisdictionMoreClick(e);
        events["click " + dom.RETURN_BUTTON] = (e) => this.settingsReturnClick(e);
        events["click .jd-override-link"] = (e) => this.overrideLinkClick(e);
        events["click " + dom.SAVE_ORG_SETTINGS] = (e) => this.saveOrganisationSettingsClick(e);
        events["click " + dom.SELECT_ALL_BUTTON] = (e) => this.selectAllClicked(e);
        return events;
    }

    public selectAllClicked(e: JQueryEventObject) {
        const allSelected = this.model.areAllSelected();
        const newVal = !allSelected;

        this.model.CoreJurisdictions.each((j) => {
            j.Selected = newVal;

        });
        this.model.IncludeAllOtherJurisdictions = newVal;
        _.defer(() => {
            this.model.saveAndNotify();
        });

        this.updateSelectAllButton();
    }

    public regions(): any {
        const regions: any = {
            loadingRegion: dom.LOADING_REGION
        };
        return regions;
    }

    public onShow() {
        this.setupLoader();
    }

    public renderSettings() {
        this.render();
        this.model.setInitialSection();
        this.$(dom.CONTENT_REGION).show();
        this.updateParentCheckboxes();
        this.updateSelectOthersCheckbox();
        this.updateSelectAllButton();
        this.setupUIDrivenModelEventListeners();
    }

    private setupModelChangeListeners() {
        this.listenTo(this.model, "change:Section", this.sectionChanged);
        this.listenTo(this.model, "change:IncludeAllOtherJurisdictions", this.renderSettings);
        this.listenTo(this.model.CoreJurisdictions, "change:Selected", this.updateSelectAllButton);
    }

    private updateSelectAllButton() {
        const text = this.model.areAllSelected() ? "Deselect all" : "Select All";
        this.$("#select-all-jurisdictions").parent().find(".jurisdiction-title").text(text).prop("checked", this.model.areAllSelected());
    }

    private overrideLinkClick(e) {
        e.preventDefault();
        const returnUrl = window.location.pathname;
        const signinUrl = `override?return=${returnUrl}`;
        Backbone.history.navigate(signinUrl, { trigger: true });
    }

    private setLevel(name: string): void {
        const classes = this.$(dom.SETTINGS_CONTENT).attr("class").split(" ");
        $.each(classes, (i, c) => {
            if (c.indexOf("level") === 0) {
                this.$(dom.SETTINGS_CONTENT).removeClass(c);
            }
        });
        this.$(dom.SETTINGS_CONTENT).addClass(name);
    }

    private sectionChanged(): void {
        const section = this.model.Section;
        if (section === "jurisdictions") {
            this.setLevel("level-1");
            this.updateAriaVisibility("#parent-jurisdiction-list");
        } else if (section === "sub-jurisdictions") {
            this.setLevel("level-2");
            this.updateAriaVisibility("#child-jurisdiction-lists");
        } else if (section === "top") {
            this.setLevel("");
            this.updateAriaVisibility(null);
        }
    }

    private setupUIDrivenModelEventListeners() {
        const allJurisdictions = this.model.getJurisdictionsWithChildren();
        this.listenTo(allJurisdictions, "childChanged", this.childChanged);
        this.listenTo(allJurisdictions, "change:Selected", this.updateCheckBox);
        this.listenTo(allJurisdictions, "change:Partial", this.updateCheckBox);
        allJurisdictions.each((all) => {
            all.setupUIDrivenModelEventListeners();
        });
    }

    private childChanged(model: JurisdictionModel) {
        this.updateCheckBox(model);
    }

    private updateCheckBox(model: JurisdictionModel) {
        const attr = `[data-jurisdiction="${model.JurisdictionId}"]`;
        const $chk = this.$("input[type=checkbox]").filter(attr);
        const indeterminate = model.Partial;
        const selected = model.Selected;
        $chk.prop("indeterminate", (indeterminate && !selected));
        $chk.prop("checked", selected);
        $chk.parent().parent().find(".jurisdiction-name").toggleClass("selected", selected || indeterminate);
    }

    private updateParentCheckboxes() {
        const all = this.model.CoreJurisdictions;
        all.each((parent) => {
            this.updateCheckBox(parent);
            const children = parent.Children;
            children.each((child) => {
                this.updateCheckBox(child);
            });
        });
    }

    private setupLoader() {
        this.loadingRegion.show(new ThinkerView());
    }

    private jurisdictionMoreClick(e: JQueryMouseEventObject) {
        this.$(".child-jurisdiction-list").hide();
        const id = $(e.currentTarget).attr("data-id");
        const $targetList = this.$(`.child-jurisdiction-list[data-parent="${id}"]`);
        $targetList.show();
        this.model.Section = "sub-jurisdictions";
    }

    private jurisdictionSelectClick(e: JQueryEventObject): void {
        const id = $(e.currentTarget).attr("data-jurisdiction");
        const jurisdictionModel = this.model.getJurisdictionById(id);

        jurisdictionModel.toggleSelected();

        _.defer(() => {
            this.model.saveAndNotify();
        });
    }

    private jurisdictionChildSelectClick(e: JQueryEventObject): void {
        const $chk = $(e.currentTarget);
        const id = $chk.attr("data-jurisdiction");
        const parent = $chk.closest(".child-jurisdiction-list").attr("data-parent");
        const parentModel = this.model.getJurisdictionById(parent);
        const jurisdictionModel = parentModel.getChildById(id);
        jurisdictionModel.toggleSelected();
        _.defer(() => {
            this.model.saveAndNotify();
        });
    }

    private searchBoxKeyup(e: JQueryKeyEventObject): void {
        const filterText = this.$(dom.SEARCH_BOX).val().toLowerCase();
        if (this.filterTimer !== null) { clearTimeout(this.filterTimer); }
        this.filterTimer = setTimeout(() => {
            if (filterText === "") {
                // no text, so show all the jurisdictions
                this.$("#parent-jurisdiction-list").find("li").show();
            } else {
                this.$("#parent-jurisdiction-list").find("li").not("#jurisdictions-search-container").each(function() {
                    // stopping the search box from filtering itthis
                    if ($(this).find("#parent-jurisdictions-search").length > 0) { return true; } // continue
                    const itemText = $(this).text().toLowerCase();
                    if (itemText.indexOf(filterText) === -1) {
                        $(this).hide();
                    } else {
                        $(this).show();
                    }
                });
            }
        }, 0);
    }

    private updateSelectOthersCheckbox() {
        this.$(dom.CHECK_ALL_OTHER_JURISDICTION).prop("checked", this.model.IncludeAllOtherJurisdictions);
    }

    private jurisdictionSelectOthersClick(e: JQueryEventObject) {
        const $checkbox = $(e.currentTarget);
        const selected = $checkbox.prop("checked");
        this.model.IncludeAllOtherJurisdictions = selected;
        _.defer(() => {
            this.model.saveAndNotify();
        });
    }

    private settingsReturnClick(e: JQueryEventObject) {
        this.model.Section = $(e.currentTarget).attr("data-section").toLowerCase();
        if (this.model.Section !== "menu") {
            e.stopPropagation();
        }
    }

    /** Sets all blades to be aria-hidden except the given shownBlade (if any)
     * @param shownBlade a selector for the blade to be seen as the visible blade, or null for none
     */
    private updateAriaVisibility(shownBlade: string): void {
        this.$(dom.SETTINGS_CONTENT).find(".settings-content-blade").attr("aria-hidden", true.toString());
        if (shownBlade) {
            this.$(dom.SETTINGS_CONTENT).find(shownBlade).attr("aria-hidden", false.toString());
        }
    }

    private saveOrganisationSettingsClick(e: JQueryEventObject) {
        this.model.saveOrganisationSettings();
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/settings/views/jurisdiction-settings.ts